import React, { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    MAX_CONTENT_WIDTH,
    GRID_FIXED_PADDING,
    GRID_FIXED_MOBILE_PADDING
} from "../../../../../uniform/components/column-layout";
import { BgColors } from "../../../components/link/src/link";

import { DraggableTab, DraggableTabList, TabPanel, Tabs } from "./";

export type TabDataType = {
    id: string;
    title: string;
    tabComponent: React.ReactElement;
};

export type TabsSectionProps = PropsWithChildren<{
    readonly headerText: string;
    readonly alignment?: "left" | "center";
    readonly tabsData: TabDataType[];
    readonly tabSize?: "small" | "large";
    readonly bgColor?: BgColors;
}>;

export const TabsSection = ({
    headerText,
    alignment = "left",
    tabsData,
    tabSize = "large",
    bgColor = "bgWhite"
}: TabsSectionProps) => {
    const headerStyles = css({
        width: "100%",
        margin: 0,
        textAlign: alignment
    });

    const sectionStyles = css({
        width: "100%",
        overflow: "hidden"
    });

    const fullWidthContainerStyles = css({
        margin: "auto",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "visible",
        paddingInlineStart: `${GRID_FIXED_MOBILE_PADDING}px`,
        paddingInlineEnd: `${GRID_FIXED_MOBILE_PADDING}px`,
        width: "100%",
        [media.only.sm]: {
            paddingInlineStart: `${GRID_FIXED_PADDING}px`,
            paddingInlineEnd: `${GRID_FIXED_PADDING}px`
        },
        [media.above.md]: {
            paddingInlineStart: `${GRID_FIXED_PADDING}px`,
            paddingInlineEnd: `${GRID_FIXED_PADDING}px`,
            maxWidth: `${MAX_CONTENT_WIDTH + GRID_FIXED_PADDING * 2}px`
        }
    });

    return (
        <section id="tabs" css={sectionStyles}>
            <Tabs>
                <div css={tabSize === "large" && fullWidthContainerStyles}>
                    <h2 css={headerStyles}>{headerText}</h2>
                </div>
                <DraggableTabList tabSize={tabSize} alignment={alignment}>
                    {tabsData.map((tab, index) => (
                        <DraggableTab
                            key={index}
                            tabIndex={index}
                            title={tab.title}
                            tabSize={tabSize}
                            bgColor={bgColor}
                            className="round-tab"
                        />
                    ))}
                </DraggableTabList>
                <div
                    css={tabSize === "large" && fullWidthContainerStyles}
                    className="tab-panels"
                >
                    {tabsData.map((tab, index) => (
                        <TabPanel panelIndex={index} id={tab.id} key={index}>
                            {tab.tabComponent}
                        </TabPanel>
                    ))}
                </div>
            </Tabs>
        </section>
    );
};
